/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useCourseProgressSaveMutation,
  useCourseProgressGetQuery,
} from "gql/generated";
import { Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import { usePostHog } from "shared/hooks";

interface ScormPlayerProps {
  courseUrl: string;
  courseTitle: string;
  resourceId: string;
  broadcastId: string;
  onDataChange?: (data: Record<string, string>) => void;
  onInitialize?: () => void;
  onTerminate?: () => void;
  isPreview?: boolean;
}

/**
 * SCORM Player Component
 * Renders a course in an iframe and provides SCORM API for communication
 */
export const ScormPlayer = ({
  courseUrl,
  courseTitle,
  resourceId,
  broadcastId,
  onDataChange,
  onInitialize,
  onTerminate,
  isPreview = false,
}: ScormPlayerProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [_scormData, setScormData] = useState<Record<string, string>>({});
  const scormDataRef = useRef<Record<string, string>>({});
  const lastErrorRef = useRef<string>("0");
  const scormVersionRef = useRef<string | null>(null);
  const { t } = useTranslation("core");
  const { trackPostHogException } = usePostHog();

  // GraphQL hooks
  const [saveProgress] = useCourseProgressSaveMutation();
  const {
    data: existingProgress,
    loading: progressLoading,
    error: progressError,
  } = useCourseProgressGetQuery({
    variables: { resourceId, broadcastId },
    skip: !resourceId || !broadcastId,
  });

  useEffect(() => {
    if (progressError) {
      trackPostHogException(progressError, {
        fileName: "ScormPlayer.tsx",
        method: "useEffect",
        message: "Error loading progress",
        extra: {
          hint: "SCORM Player error loading progress",
          resourceId,
          broadcastId,
        },
      });
    }
  }, [progressError, resourceId, broadcastId, trackPostHogException]);

  // Load existing progress into scormData
  useEffect(() => {
    // Only process once loading is complete
    if (progressLoading) return;

    if (existingProgress?.courseProgressGet) {
      const progress = existingProgress.courseProgressGet;

      // Convert stored data back to SCORM format
      const resumeData: Record<string, string> = {};
      if (progress.scormDataSnapshot) {
        Object.entries(progress.scormDataSnapshot).forEach(([key, value]) => {
          resumeData[key] = value as string;
        });
      }

      setScormData(resumeData);
      scormDataRef.current = resumeData;
      scormVersionRef.current = progress.scormVersion || null;
    } else {
      // No existing progress found, starting fresh
    }
  }, [existingProgress, progressLoading]);

  // Save progress function to the BE
  const handleSaveProgress = useCallback(
    async (data: Record<string, string>) => {
      try {
        // Ensure we're sending a plain object, not a Map
        const scormDataObject = { ...data };

        if (!isPreview) {
          await saveProgress({
            variables: {
              resourceId,
              broadcastId,
              scormData: scormDataObject,
              scormVersion: scormVersionRef.current || undefined,
            },
          });
        }
      } catch (error) {
        trackPostHogException(error, {
          fileName: "ScormPlayer.tsx",
          method: "handleSaveProgress",
          message: "Error saving progress",
          extra: {
            hint: "SCORM Player error saving progress",
            resourceId,
            broadcastId,
          },
        });
      }
    },
    [resourceId, broadcastId, saveProgress, isPreview, trackPostHogException],
  );

  /**
   * Expose SCORM API to window for course to find
   */
  useEffect(() => {
    // Create SCORM 2004 API
    const API_1484_11 = {
      Initialize: (_param: string) => {
        onInitialize?.();
        lastErrorRef.current = "0";
        return "true";
      },
      Terminate: (_param: string) => {
        onTerminate?.();
        lastErrorRef.current = "0";
        return "true";
      },
      GetValue: (element: string) => {
        const value = scormDataRef.current[element] || "";
        lastErrorRef.current = "0";
        return value;
      },
      SetValue: (element: string, value: string) => {
        setScormData(prev => {
          const newData = { ...prev, [element]: value };
          scormDataRef.current = newData;
          onDataChange?.(newData);
          return newData;
        });
        lastErrorRef.current = "0";
        return "true";
      },
      Commit: (_param: string) => {
        lastErrorRef.current = "0";
        scormVersionRef.current = "2004";
        handleSaveProgress(scormDataRef.current);
        return "true";
      },
      GetLastError: () => {
        return lastErrorRef.current;
      },
      GetErrorString: (errorCode: string) => {
        const errors: Record<string, string> = {
          "0": "No error",
          "101": "General exception",
          "102": "General initialization failure",
          "103": "Already initialized",
          "104": "Content instance terminated",
          "111": "General termination failure",
          "112": "Termination before initialization",
          "113": "Termination after termination",
          "122": "Retrieve data before initialization",
          "123": "Retrieve data after termination",
          "132": "Store data before initialization",
          "133": "Store data after termination",
          "142": "Commit before initialization",
          "143": "Commit after termination",
        };
        return errors[errorCode] || "";
      },
      GetDiagnostic: (errorCode: string) => {
        return `Diagnostic for error ${errorCode}`;
      },
    };

    // Create SCORM 1.2 API
    const API = {
      LMSInitialize: (_param: string) => {
        onInitialize?.();
        lastErrorRef.current = "0";
        return "true";
      },
      LMSFinish: (_param: string) => {
        onTerminate?.();
        lastErrorRef.current = "0";
        return "true";
      },
      LMSGetValue: (element: string) => {
        const value = scormDataRef.current[element] || "";
        lastErrorRef.current = "0";
        return value;
      },
      LMSSetValue: (element: string, value: string) => {
        setScormData(prev => {
          const newData = { ...prev, [element]: value };
          scormDataRef.current = newData;
          onDataChange?.(newData);
          return newData;
        });
        lastErrorRef.current = "0";
        return "true";
      },
      LMSCommit: (_param: string) => {
        lastErrorRef.current = "0";
        scormVersionRef.current = "1.2";
        handleSaveProgress(scormDataRef.current);
        return "true";
      },
      LMSGetLastError: () => {
        return lastErrorRef.current;
      },
      LMSGetErrorString: (errorCode: string) => {
        const errors: Record<string, string> = {
          "0": "No error",
          "101": "General exception",
          "201": "Invalid argument error",
          "202": "Element cannot have children",
          "203": "Element not an array - cannot have count",
          "301": "Not initialized",
          "401": "Not implemented error",
          "402": "Invalid set value, element is a keyword",
          "403": "Element is read only",
          "404": "Element is write only",
          "405": "Incorrect data type",
        };
        return errors[errorCode] || "";
      },
      LMSGetDiagnostic: (errorCode: string) => {
        return `Diagnostic for error ${errorCode}`;
      },
    };

    // Expose APIs on window
    (window as any).API_1484_11 = API_1484_11;
    (window as any).API = API;

    // Cleanup
    return () => {
      delete (window as any).API_1484_11;
      delete (window as any).API;
    };
  }, [handleSaveProgress, onDataChange, onInitialize, onTerminate]);

  // Don't render iframe until we've checked for existing progress
  if (progressLoading) {
    return (
      <Flex
        w="full"
        h="full"
        alignItems="center"
        justifyContent="center"
        bg="bg.lightest"
      >
        {t("LOADING_COURSE")}...
      </Flex>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      src={courseUrl}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
      }}
      title={courseTitle}
      allow="fullscreen"
    />
  );
};
